import { create } from "zustand";
import api, { isAbortError } from "../../utils/api";
import { getError } from "../../utils/getError";
import type { Device } from "./devicesStore";

export interface User {
  id: string;
  role_id: string;
  company_id: string;
  email: string;
  username?: string;
  first_name: string;
  last_name: string;
  phone?: string;
  address?: string;
  bio?: string;
  location?: string;
  website?: string;
  status_id: string;
  group_id?: string;
  preferences_id?: string;
  updatedAt?: { _seconds: number; _nanoseconds: number };
  createdAt?: { _seconds: number; _nanoseconds: number };
  collections?: string[];
  // Devices asignados al usuario
  available_devices?: Device[];
  devices?: Device[];
  // Enriched data from API
  role?: { id: string; name: string } | null;
  company?: { id: string; name: string } | null;
}

// Minimal user shape returned by /userList/light — enough for selects like
// "usuarios a notificar" without the heavy device/role enrichment.
export interface UserLight {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
}

interface UsersMeta {
  error: string | null;
  count: number;
  status: number;
  currentPage: number;
  totalPages: number;
  totalItems: number;
  url: string;
}

interface UsersStore {
  users: User[];
  loading: boolean;
  error: string | null;
  meta?: UsersMeta;
  // Estado ligero para selects de "usuarios a notificar" (fetchUsersLight)
  usersLight: UserLight[];
  usersLightLoading: boolean;
  fetchUsersLight: (params?: {
    search?: string;
    allCompanies?: boolean;
  }) => Promise<void>;
  fetchUsers: (params?: {
    limit?: number;
    page?: number;
    offset?: number;
    search?: string;
    status_id?: string;
    /**
     * If true, do NOT scope the request to the currently-selected company.
     * Use this only for reseller flows that legitimately need users from
     * across every company (e.g. assigning a carrier from a different
     * tenant on a trip / geofence). Internally this overrides the
     * x-company-id header with an empty string so the backend falls
     * through to the unscoped `getDocumentsLight("users")` path.
     */
    allCompanies?: boolean;
  }) => Promise<void>;
}

let usersFetchId = 0;
let usersLightFetchId = 0;

export const useUsersStore = create<UsersStore>((set) => ({
  users: [],
  loading: false,
  error: null,
  meta: undefined,
  // Estado ligero para selects
  usersLight: [],
  usersLightLoading: false,

  fetchUsersLight: async (params = {}) => {
    const fetchId = ++usersLightFetchId;
    set({ usersLightLoading: true });
    try {
      const { data } = await api.get("/userList/light", {
        params: {
          ...(params.search && params.search.trim() !== ""
            ? { search: params.search.trim() }
            : {}),
        },
        // Mismo flag que fetchUsers: omite el scope de compañía para traer
        // usuarios de todas (reseller + carriers).
        ...(params.allCompanies ? { _skipCompanyScope: true } : {}),
      } as Parameters<typeof api.get>[1]);
      if (fetchId !== usersLightFetchId) return;
      set({
        usersLight: Array.isArray(data.data) ? data.data : [],
        usersLightLoading: false,
      });
    } catch (err: any) {
      if (fetchId !== usersLightFetchId || isAbortError(err)) return;
      set({ usersLightLoading: false });
    }
  },

  fetchUsers: async (params = {}) => {
    const fetchId = ++usersFetchId;

    set({ loading: true, error: null });
    try {
      const { data } = await api.get("/userList", {
        params: {
          limit: params.limit ?? 8,
          page: params.page ?? 1,
          ...(params.search && params.search.trim() !== "" && { search: params.search.trim() }),
          ...(params.status_id && params.status_id !== "all" && { status_id: params.status_id }),
        },
        // Bandera consumida por el interceptor en `api.ts`: cuando es `true`
        // se omite la inyección automática de `x-company-id` (header) y de
        // `company_id` (query param), de modo que el backend cae al path
        // `getDocumentsLight("users")` y devuelve usuarios de TODAS las
        // compañías. Indispensable para resellers que asignan/notifican a
        // usuarios fuera de su tenant. El truco anterior con string vacío
        // no funcionaba porque el chequeo falsy del interceptor lo
        // sobreescribía con el companyId del localStorage.
        ...(params.allCompanies ? { _skipCompanyScope: true } : {}),
      } as Parameters<typeof api.get>[1]);
      if (fetchId !== usersFetchId) return;
      set({ users: data.data || [], meta: data.meta, loading: false });
    } catch (err: any) {
      if (fetchId !== usersFetchId || isAbortError(err)) return;
      set({ error: getError(err), loading: false });
    }
  },
}));
